/**
 * O cliente de API dos três apps.
 *
 * Existe uma vez, em `web/ui`, e não uma cópia por app — porque as decisões daqui não são de
 * transporte, são de comportamento de produto, e três cópias divergem na primeira pressa.
 *
 * Três coisas que este arquivo resolve e que, feitas errado, viram bug em produção:
 *
 * 1. **503 não desloga.** É a contraparte da decisão do H9 no servidor: Cert4All fora do ar vira
 *    503 com `Retry-After`, e só 401 significa "sua sessão morreu". Tratar os dois igual aqui
 *    jogaria fora metade daquele trabalho.
 * 2. **`Accept-Version`.** Sem ele, deploy de servidor derruba painel aberto no navegador do
 *    cliente (§6.5 da spec 02).
 * 3. **A forma única de erro.** `codigo_estavel` decide comportamento; `mensagem` é o que a tela
 *    mostra. Nunca o contrário — §6.2.
 */

/** A versão de contrato que este bundle entende. Ver §6.5 da spec 02. */
export const VERSAO_DO_CONTRATO = "2026-08-11";

/**
 * A forma única de erro do §6.2 da spec 02.
 *
 * `mensagem` é para o operador e diz **o que fazer** — mesma regra do `Veredito.explicacao` do
 * domínio. `erro` é código estável, para o front decidir comportamento.
 */
export interface ErroDaApi {
  readonly erro: string;
  readonly mensagem?: string;
  readonly detalhe?: unknown;
  readonly traceId?: string;
}

/**
 * O que deu errado, classificado pelo que a TELA deve fazer — não pelo status HTTP.
 *
 * A classificação é o valor deste tipo. Um componente que recebe `status: 503` tem de reimplementar
 * a decisão; um que recebe `tipo: "indisponivel"` já sabe que oferece "tentar de novo" e **não**
 * manda ninguém para o login.
 */
export type FalhaDaApi =
  /** 401. A sessão morreu de verdade: o app deve ir para o login. */
  | { readonly tipo: "sem_sessao"; readonly erro: ErroDaApi }
  /** 503, ou rede. **Não é sessão inválida.** Oferecer repetir, preservando o que foi digitado. */
  | { readonly tipo: "indisponivel"; readonly erro: ErroDaApi; readonly tentarEmSegundos: number | null }
  /** 403 por permissão. A tela diz **qual** permissão falta (§8, "Sem permissão"). */
  | { readonly tipo: "sem_permissao"; readonly erro: ErroDaApi; readonly permissao: string | null }
  /** 403 por licença. É caminho comercial, não erro (§5, regra 2). */
  | { readonly tipo: "sem_entitlement"; readonly erro: ErroDaApi }
  /** 404. */
  | { readonly tipo: "nao_encontrado"; readonly erro: ErroDaApi }
  /** 409, 422 — o servidor recusou por regra de negócio. A `mensagem` é acionável. */
  | { readonly tipo: "recusado"; readonly erro: ErroDaApi }
  /** O bundle é mais velho que o servidor. Exige recarregar — §6.5. */
  | { readonly tipo: "versao_incompativel"; readonly erro: ErroDaApi }
  /** Qualquer outra coisa. */
  | { readonly tipo: "inesperado"; readonly erro: ErroDaApi; readonly status: number };

export class FalhaHttp extends Error {
  constructor(readonly falha: FalhaDaApi) {
    super(falha.erro.mensagem ?? falha.erro.erro);
    this.name = "FalhaHttp";
  }
}

/** `true` quando repetir tem chance de resolver — usado pelo `retry` do TanStack Query. */
export function valeRepetir(falha: FalhaDaApi): boolean {
  return falha.tipo === "indisponivel";
}

function segundosDe(cabecalho: string | null): number | null {
  if (!cabecalho) return null;
  const n = Number(cabecalho);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

function classificar(status: number, erro: ErroDaApi, retryAfter: string | null): FalhaDaApi {
  if (status === 401) return { tipo: "sem_sessao", erro };

  if (status === 503) {
    return { tipo: "indisponivel", erro, tentarEmSegundos: segundosDe(retryAfter) };
  }

  if (status === 403) {
    // Dois 403 diferentes, e a tela faz coisas opostas: um oferece o caminho comercial, o outro diz
    // qual permissão falta e quem pode conceder. Um 403 genérico faria as duas telas serem a mesma.
    if (erro.erro === "PRODUTO_NAO_CONTRATADO") return { tipo: "sem_entitlement", erro };
    // De `detalhe`, e nao do topo: a forma unica do §6.2 tem quatro campos, e ler um quinto faria
    // o front depender de algo que a spec nao promete.
    const detalhe = erro.detalhe as { permissao?: unknown } | undefined;
    const permissao = typeof detalhe?.permissao === "string" ? detalhe.permissao : null;
    return { tipo: "sem_permissao", erro, permissao };
  }

  if (status === 404) return { tipo: "nao_encontrado", erro };
  if (status === 409 || status === 422) return { tipo: "recusado", erro };
  if (status === 426 || erro.erro === "VERSAO_INCOMPATIVEL") {
    return { tipo: "versao_incompativel", erro };
  }
  return { tipo: "inesperado", erro, status };
}

export interface OpcoesDeChamada {
  // `PUT` entrou em 2026-08-13: a troca de perfil de acesso substitui um estado
  // inteiro (o papel da pessoa no Cert4All), e a rota de la e PUT.
  readonly metodo?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  readonly corpo?: unknown;
  readonly sinal?: AbortSignal;
  /**
   * Cabeçalhos por chamada. Hoje o único uso é `Idempotency-Key`, no envio de mensagem.
   *
   * **Não serve para autenticação**: a sessão é cookie `HttpOnly` (§11 da spec 02), e mandar
   * `Authorization` daqui exigiria um token legível por JavaScript — que é a coisa que aquele §
   * proíbe. Um `Authorization` passado aqui seria sobrescrito, e é melhor assim.
   */
  readonly cabecalhos?: Readonly<Record<string, string>>;
}

/**
 * Chama a API.
 *
 * `credentials: "same-origin"` e nada de `Authorization`: a sessão é **cookie `HttpOnly`** (§11 da
 * spec 02). Token em `localStorage` é XSS que vira sequestro de sessão, e nem daria para ler daqui.
 */
/**
 * A EMPRESA ABERTA POR ACESSO DE SUPORTE (0067) — guardada por aba, e não por navegador.
 *
 * ## `sessionStorage`, e a escolha é o contrário do resto do produto
 *
 * A barra recolhida e o tema usam `localStorage`, porque são preferências que a pessoa quer de
 * volta amanhã. Isto é o oposto: **acesso de suporte não pode sobreviver ao fechar do navegador**.
 * Guardado em `localStorage`, o operador voltaria dias depois direto na conta de um cliente, sem
 * perceber, e editaria achando que é a dele — que é exatamente o desfecho que este mecanismo
 * inteiro existe para impedir.
 *
 * E por ABA: abrir a conta da cliente numa aba e a própria noutra é o uso natural, e o
 * `sessionStorage` é por aba.
 */
const CHAVE_DE_SUPORTE = "hugi:suporte:empresa";

export function empresaDeSuporte(): string | null {
  try {
    return sessionStorage.getItem(CHAVE_DE_SUPORTE) || null;
  } catch {
    // Aba anônima com armazenamento bloqueado. Sem acesso de suporte é o desfecho seguro.
    return null;
  }
}

export function abrirComoSuporte(companyId: string | null): void {
  try {
    if (companyId) sessionStorage.setItem(CHAVE_DE_SUPORTE, companyId);
    else sessionStorage.removeItem(CHAVE_DE_SUPORTE);
  } catch {
    /* idem */
  }
}

export async function chamar<T>(caminho: string, opcoes: OpcoesDeChamada = {}): Promise<T> {
  const suporte = empresaDeSuporte();
  let resposta: Response;
  try {
    resposta = await fetch(caminho, {
      method: opcoes.metodo ?? "GET",
      credentials: "same-origin",
      headers: {
        ...opcoes.cabecalhos,
        // Depois do espalhamento, de propósito: os três são do contrato, e um cabeçalho por chamada
        // não pode desligar a negociação de versão, mandar corpo com o tipo errado — nem, agora,
        // tirar o acesso de suporte de UMA requisição e deixar as outras dentro da conta do cliente.
        "Accept-Version": VERSAO_DO_CONTRATO,
        ...(suporte ? { "x-hugi-suporte": suporte } : {}),
        ...(opcoes.corpo === undefined ? {} : { "Content-Type": "application/json" }),
      },
      ...(opcoes.corpo === undefined ? {} : { body: JSON.stringify(opcoes.corpo) }),
      ...(opcoes.sinal ? { signal: opcoes.sinal } : {}),
    });
  } catch (causa) {
    // Rede caída, DNS, offline. Mesma família do 503: **não é sessão inválida.**
    throw new FalhaHttp({
      tipo: "indisponivel",
      erro: {
        erro: "SEM_REDE",
        mensagem: "Não foi possível falar com o servidor. Verifique a conexão e tente de novo.",
        detalhe: causa instanceof Error ? causa.message : String(causa),
      },
      tentarEmSegundos: null,
    });
  }

  if (resposta.ok) {
    if (resposta.status === 204) return undefined as T;
    return (await resposta.json()) as T;
  }

  // Corpo que não é JSON acontece de verdade: página de proxy, HTML de erro de gateway. Deixar o
  // `.json()` estourar produziria "Unexpected token <" na tela do cliente.
  const erro: ErroDaApi = await resposta
    .json()
    .then((c) => (c && typeof c === "object" ? (c as ErroDaApi) : { erro: "RESPOSTA_INVALIDA" }))
    .catch(() => ({
      erro: "RESPOSTA_INVALIDA",
      mensagem: `O servidor respondeu ${resposta.status} sem detalhe.`,
    }));

  throw new FalhaHttp(classificar(resposta.status, erro, resposta.headers.get("Retry-After")));
}

/** A falha, ou `null` se o erro não veio daqui. Para o componente decidir sem `instanceof` espalhado. */
export function falhaDe(erro: unknown): FalhaDaApi | null {
  return erro instanceof FalhaHttp ? erro.falha : null;
}
